import { circle as turfCircle } from '@turf/turf';

import { ringLonLatFromGeoSource } from '../utilities/normalizeGeoShape';
import { geometryBounds } from './geometryBounds';

// Re-exported so the callers that ask an area for its extent keep one import.
export { geometryBounds };

/**
 * The area a heatmap is drawn for, as GeoJSON in WGS84.
 *
 * Three things can define it — a radius around an address, a postal code district, a town — and
 * each stores its outline differently. Everything downstream (the red outline on the map, the
 * payload of `POST /metrics/tiles`) wants one shape, so the conversion happens here once.
 */

export type AreaKind = 'address' | 'postalCode' | 'locality';

export interface AreaSelection {
    kind: AreaKind;
    /** One line naming the area, for the map legend and the section header. */
    label: string;
    geometry: any;
    /** Set for the address case — the map centres on it and the slider belongs to it. */
    centre?: { lat: number; lon: number };
    radiusKm?: number;
}

/** Largest radius the slider offers. Beyond this a request covers more cells than the map can show. */
export const MAX_RADIUS_KM = 5;
export const DEFAULT_RADIUS_KM = 1;

/**
 * A circle as a polygon. The tile endpoint takes a polygon, not a centre and a radius — the server
 * has one way of clipping, and a circle is just a polygon with enough corners. 128 steps keeps the
 * edge smooth at the zoom a 5 km radius is looked at.
 */
export const circleGeometry = (lat: number, lon: number, radiusKm: number): any =>
    turfCircle([lon, lat], radiusKm, { steps: 128, units: 'kilometers' }).geometry;

const isPointPair = (value: any): boolean =>
    Array.isArray(value) && value.length >= 2 && typeof value[0] === 'number' && typeof value[1] === 'number';

const ringFromPoints = (points: any[]): number[][] | null => {
    const ring = points
        .map((point: any) => {
            if (isPointPair(point)) return [point[0], point[1]];
            if (point && typeof point.lon === 'number' && typeof point.lat === 'number') {
                return [point.lon, point.lat];
            }
            if (point && typeof point.lng === 'number' && typeof point.lat === 'number') {
                return [point.lng, point.lat];
            }
            return null;
        })
        .filter((coordinate): coordinate is number[] => coordinate !== null);

    if (ring.length < 3) return null;

    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
        ring.push([first[0], first[1]]);
    }
    return ring;
};

/**
 * Coordinates the way GeoJSON means them: longitude first.
 *
 * The outlines of postal codes and administrative units are stored latitude
 * first. The map hides that — it swaps back when it draws — so the red outline
 * always looked right while the geometry itself was inside out, and everything
 * that *computed* with it rather than drawing it got a box in the Indian Ocean:
 * the server's area queries returned nothing at all for a town.
 *
 * Anything above 20 cannot be a longitude here while being a plausible latitude —
 * Germany spans 5.8°–15.1° east and 47°–55° north, so the two ranges do not
 * overlap. Correct data passes through untouched, which is what makes it safe to
 * apply to every branch below.
 */
const toLonLat = (node: any): any => {
    if (!Array.isArray(node) || node.length === 0) return node;
    if (Array.isArray(node[0])) return node.map(toLonLat);

    if (typeof node[0] === 'number' && typeof node[1] === 'number') {
        return node[0] > 20 ? [node[1], node[0]] : node;
    }

    return node;
};

const asGeoJson = (geometry: any | null): any | null =>
    geometry ? { ...geometry, coordinates: toLonLat(geometry.coordinates) } : null;

/**
 * Outline of a PostalCode or LocalGovernment as GeoJSON.
 *
 * A town is often a MultiPolygon — an exclave, an island, a district cut out of another. The
 * single-ring helper used elsewhere in the portal would silently keep the first ring and drop the
 * rest, which for a heatmap means quietly leaving out part of the town, so the multi-part cases are
 * handled here before falling back to it.
 */
export const areaGeometryFromEntity = (entity: any): any | null => {
    if (!entity) return null;

    const candidates = [entity.geometry, entity.shape];

    for (const candidate of candidates) {
        if (!candidate) continue;

        const value = typeof candidate === 'string' ? safeParse(candidate) : candidate;
        if (!value) continue;

        if (value.type === 'Polygon' || value.type === 'MultiPolygon') {
            if (Array.isArray(value.coordinates) && value.coordinates.length > 0) {
                return asGeoJson(value);
            }
        }

        if (Array.isArray(value) && value.length > 0) {
            // A flat list of points is one ring; a list of lists is several.
            if (isPointPair(value[0]) || (value[0] && typeof value[0].lat === 'number')) {
                const ring = ringFromPoints(value);
                return ring ? asGeoJson({ type: 'Polygon', coordinates: [ring] }) : null;
            }

            // A list of polygons, each a list of rings (outer ring, then holes): Hamburg with Neuwerk and the Elbe islands.
            if (Array.isArray(value[0]?.[0]) && isPointPair(value[0][0][0])) {
                const polygons = value
                    .map((polygon: any) =>
                        (Array.isArray(polygon) ? polygon : [])
                            .map((ring: any) => ringFromPoints(Array.isArray(ring) ? ring : []))
                            .filter((ring: number[][] | null): ring is number[][] => ring !== null),
                    )
                    .filter((rings: number[][][]) => rings.length > 0);

                if (polygons.length === 1) return asGeoJson({ type: 'Polygon', coordinates: polygons[0] });
                if (polygons.length > 1) return asGeoJson({ type: 'MultiPolygon', coordinates: polygons });
            }

            if (Array.isArray(value[0])) {
                const polygons = value
                    .map((part: any) => ringFromPoints(Array.isArray(part) ? part : []))
                    .filter((ring): ring is number[][] => ring !== null)
                    .map((ring) => [ring]);

                if (polygons.length === 1) return asGeoJson({ type: 'Polygon', coordinates: polygons[0] });
                if (polygons.length > 1) return asGeoJson({ type: 'MultiPolygon', coordinates: polygons });
            }
        }
    }

    // Last resort: the portal's shared single-ring reader, which copes with shapes this does not.
    const ring = ringLonLatFromGeoSource(entity);
    return ring && ring.length >= 3 ? asGeoJson({ type: 'Polygon', coordinates: [closeRing(ring)] }) : null;
};

const closeRing = (ring: number[][]): number[][] => {
    const first = ring[0];
    const last = ring[ring.length - 1];
    return first[0] === last[0] && first[1] === last[1] ? ring : [...ring, [first[0], first[1]]];
};

const safeParse = (value: string): any => {
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

/** Bounding box [[minLon, minLat], [maxLon, maxLat]] of a geometry, for framing the map. */
const OUTLINE: Record<string, unknown> = {
    color: '#C81E1E',
    fillColor: '#C81E1E',
    fillOpacity: 0.04,
    weight: 2.5,
    opacity: 0.95,
};

/**
 * The selected area drawn in red — the frame the tiles sit in.
 *
 * A radius is drawn as a real circle, not as the polygon that was sent to the server: the map can
 * draw a circle itself, and a circle stays round at every zoom where a 128-corner approximation
 * starts to show its corners. The polygon remains the thing the server clips by — one shape for
 * the query, the honest shape for the eye.
 */
export const areaOutlineElement = (area: AreaSelection) => {
    if (area.kind === 'address' && area.centre && area.radiusKm) {
        return {
            display: {
                type: 'circle',
                lat: area.centre.lat,
                lon: area.centre.lon,
                radius: area.radiusKm * 1000,
                ...OUTLINE,
            },
        };
    }

    return { display: { type: 'shape', shape: area.geometry, ...OUTLINE } };
};
